import WolfSectionColor from '@components/ui/WolfSectionColor';
import WolfStepIndicator from '@components/ui/WolfStepIndicator';
import WolfTooltip from '@components/ui/WolfTooltip';
import { useTranslation } from '@hooks/useTranslation';
import { NodeData } from '@models/process-map.model';
import { ProcessStatus } from '@models/process.model';
import { StepType } from '@models/step.model';
import { AddCircleOutline, ExpandLess, ExpandMore, GroupWorkOutlined } from '@mui/icons-material';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { filterStepsInsideSwitchRepeat } from '@utils/filterStepsInsideSwitchRepeat';
import { getStepsInsideSwitchRepeat } from '@utils/getStepsInsideSwitchRepeat';
import { isColorDark } from '@utils/isColorDark';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Handle, Position } from 'reactflow';

interface ProcessNodeProps {
  data: NodeData;
  id: string;
  expand?: boolean;
}

export const ProcessNode = ({ data, id, expand }: ProcessNodeProps) => {
  const localeProcMap = useTranslation('processMap');
  const localeCommon = useTranslation('common');
  const dark = isColorDark(data.backgroundColor || '#FFFFFF');
  const [expanded, setExpanded] = useState(!!expand);
  const [isDrawn, setIsDrawn] = useState(false);
  const history = useHistory();

  // Construct the correct categoryId from process data if available
  const categoryId = data.isExternal && data.process
    ? `${data.process.service?.serviceId}---${data.process.category || localeCommon['uncategorized']}`
    : data.categoryId;

  useEffect(() => {
    if (data.expand !== undefined) {
      setExpanded(data.expand);
    }
  }, [data.expand]);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleUnifiedClick = () => {
    if (!isDrawn) {
      // First click: Mark as drawn to enable link functionality
      setIsDrawn(true);
      // Event will bubble up to trigger category-add action via data-action attribute
    } else {
      // Second click: Show link temporarily
      setTimeout(() => {
      }, 2000);
      // Event will bubble up to trigger category-show-link action via data-action attribute
    }
  };

  const handleProcessClick = (e: React.MouseEvent) => {
    // Only navigate for internal processes (not external references)
    if (!data.isExternal && data.processId) {
      e.stopPropagation();
      history.push(`/processes/${data.processId}`);
    }
  };

  return (
    <div
      className={
        'relative flex items-center shadow-lg rounded-lg border border-additionalBorder bg-white ' +
        (data.isExternal ? (data.isTrigger ? 'w-96' : 'w-[32rem]') : 'w-72')
      }>
      <Box
        sx={{ backgroundColor: data.backgroundColor + '55' }}
        className={'p-4 w-full ' + (expanded ? 'h-auto' : 'h-18')}>
        <Handle type="target" id="left" position={Position.Left} />
        <Handle type="target" id="top" position={Position.Top} />
        <Handle type="target" id="bottom" position={Position.Bottom} />
        <Handle type="source" id="right" position={Position.Right} />
        <Handle type="source" id="bottom-source" position={Position.Bottom} />

        {/* Node Content */}
        <Box className="flex items-center justify-between w-full">
          <Box className="flex items-center">
            <Box className="relative w-10 h-10 rounded-full flex items-center justify-center mr-2">
              <WolfSectionColor icon={<GroupWorkOutlined />} color={data.color || '#000000'} />
              {data?.status === ProcessStatus.DRAFT && (
                <WolfTooltip title={localeProcMap['processDraft'] || 'This process is still a Draft'}>
                  <Box className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-utilityPink flex items-center justify-center shadow-md">
                    <Typography className="!text-white !text-xs !font-bold">D</Typography>
                  </Box>
                </WolfTooltip>
              )}
            </Box>
            <Box className="flex flex-col">
              <Box className="flex flex-wrap items-center">
                {data.preLabel && (
                  <Typography
                    className={`w-100 text-xs break-all ${dark ? 'text-white' : 'text-black'}`}
                    component="span"
                    title={data?.preLabel}>
                    {data.isExternal
                      ? data?.preLabel?.slice(0, 60) + (data?.preLabel?.length > 60 ? '...' : '') + ' - '
                      : data?.preLabel?.slice(0, 35) + (data?.preLabel?.length > 35 ? '...' : '') + ' - '}
                  </Typography>
                )}
                <Typography
                  className={`!font-bold break-all ${dark ? 'text-white' : 'text-black'} ${!data.isExternal && data.processId ? 'cursor-pointer hover:underline' : ''}`}
                  component="span"
                  data-type="process"
                  data-id={id}
                  data-origin="process-name"
                  title={data.label}
                  onClick={handleProcessClick}>
                  {data.isExternal
                    ? data.label.slice(0, 60) + (data.label.length > 60 ? '...' : '')
                    : data.label.slice(0, 35) + (data.label.length > 35 ? '...' : '')}
                </Typography>
              </Box>
              {data.goal && expanded && (
                <Typography
                  className={`text-xs mt-1 break-words ${dark ? 'text-gray-200' : 'text-gray-600'}`}
                  component="div"
                  title={data.goal}>
                  {data.goal}
                </Typography>
              )}
            </Box>
          </Box>

          {!data.isExternal && (
            <Box className="flex items-center">
              {/* Expand/Collapse button */}
              <div
                onClick={handleExpand}
                className="cursor-pointer"
                data-id={id}
                data-expanded={expanded.toString()}
                data-action="individual-expand">
                <WolfTooltip title={expanded ? localeProcMap['collapse'] : localeProcMap['showAllSteps']}>
                  <IconButton className="!p-2">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </WolfTooltip>
              </div>
            </Box>
          )}
          {data.isExternal && !data.isTrigger && (
            <Box className="flex items-center">
              <div
                className="cursor-pointer"
                data-id={categoryId}
                data-origin={id}
                data-action={isDrawn ? 'category-show-link' : 'category-add'}
                onClick={handleUnifiedClick}>
                <WolfTooltip title={isDrawn ? localeProcMap['showFlowTooltip'] : localeProcMap['addFlowTooltip']}>
                  <IconButton className="!p-1">
                    <AddCircleOutline />
                  </IconButton>
                </WolfTooltip>
              </div>
            </Box>
          )}
        </Box>

        {/* Steps List */}
        {data.steps && (
          <Collapse in={expanded}>
            <Box className="mt-4 pl-4">
              {filterStepsInsideSwitchRepeat(data.steps).map((step, idx) => (
                <div key={step.stepId}>
                  <Box className={`flex items-center py-2`}>
                    <Box className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-200 mr-3">
                      <WolfStepIndicator
                        text={(idx + 1).toString()}
                        type={step.stepType}
                        isSub={false}
                        isProcessMap={true}
                      />
                    </Box>
                    <Typography className="!ml-4 text-sm max-w-[180px] break-words">{step.name}</Typography>
                  </Box>
                  {[StepType.REPEAT, StepType.SWITCH].includes(step.stepType) &&
                    getStepsInsideSwitchRepeat(step?.stepData?.stepsIds, data.steps).map((subStep, subIdx) => (
                      <Box key={subStep.stepId} className={`flex items-center py-2 ml-4`}>
                        <Box className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-200 mr-3">
                          <WolfStepIndicator
                            text={(idx + 1).toString() + '.' + (subIdx + 1).toString()}
                            type={step.stepType} // We use the stepType of the parent step to display the icon
                            isSub={false}
                            isProcessMap={true}
                          />
                        </Box>
                        <Typography className="!ml-4 text-sm max-w-[160px] break-words">{subStep.name}</Typography>
                      </Box>
                    ))}
                </div>
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    </div>
  );
};

export const AuxNodeProcessLegend: React.FC<{
  data: NodeData;
}> = ({ data }) => {
  const dark = isColorDark(data.backgroundColor || '#FFFFFF');

  return (
    <div className="relative flex items-center shadow-lg rounded-lg border border-additionalBorder bg-white max-w-72">
      <Box sx={{ backgroundColor: data.backgroundColor + '55' }} className="h-18 w-full">
        {/* Node Content */}
        <Box className="flex items-center justify-between w-full p-4 ">
          <Box className="flex items-center">
            <Box className="w-10 h-10 rounded-full flex items-center justify-center mr-2">
              <WolfSectionColor icon={<GroupWorkOutlined />} color={data.color || '#000000'} />
            </Box>
            <Box className="flex flex-col mr-10">
              <Box className="flex flex-wrap items-center">
                {data.preLabel && (
                  <Typography
                    className={`w-100 text-xs break-words max-w-[180px] ${dark ? 'text-white' : 'text-black'}`}
                    component="span"
                    title={data?.preLabel}>
                    {data?.preLabel + ' - '}
                  </Typography>
                )}
                <Typography
                  className={`!font-bold break-words max-w-[200px] ${dark ? 'text-white' : 'text-black'}`}
                  component="span"
                  title={data.label}>
                  {data.label}
                </Typography>
              </Box>
              {data.goal && (
                <Typography
                  className={`text-xs mt-1 break-words ${dark ? 'text-gray-200' : 'text-gray-600'}`}
                  component="div"
                  title={data.goal}>
                  {data.goal}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default ProcessNode;
